import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { BellIcon, BellSlashIcon } from '@heroicons/react/24/outline';
import { requestNotificationPermission } from '../../services/notificationService';
import { userSettingsApi } from '../../utils/api';

const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    sound: true,
    newMessage: true,
    mentions: true,
    status: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await userSettingsApi.getCurrent();
      const data = response.data?.data;

      if (data?.notification_settings) {
        setSettings(data.notification_settings);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      setError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key) => {
    try {
      if (key === 'enabled') {
        const permissionGranted = await requestNotificationPermission();
        if (!permissionGranted) {
          throw new Error('Permission de notification refusée');
        }
      }

      const newSettings = {
        ...settings,
        [key]: !settings[key],
      };

      await userSettingsApi.update({ notification_settings: newSettings });

      setSettings(newSettings);
    } catch (error) {
      console.error('Error updating notification settings:', error);
      setError('Erreur lors de la mise à jour des paramètres');
    }
  };

  // Squelette à la forme de la liste plutôt qu'un spinner générique
  if (loading) {
    return (
      <div className="animate-pulse space-y-4" aria-hidden="true">
        <div className="h-16 rounded-lg bg-gray-100" />
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center justify-between gap-4 py-2">
            <div className="space-y-2"><div className="h-3 w-28 rounded bg-gray-200" /><div className="h-3 w-56 rounded bg-gray-100" /></div>
            <div className="h-6 w-11 rounded-full bg-gray-200" />
          </div>
        ))}
      </div>
    );
  }

  /**
   * Interrupteur libellé. `disabled` grise la ligne et bloque l'interrupteur
   * lorsque l'option dépend d'un réglage parent désactivé.
   */
  const ToggleSwitch = ({ enabled, onChange, label, description, disabled = false }) => (
    <Switch.Group
      as="div"
      className={`flex items-center justify-between gap-4 transition duration-150 ${disabled ? 'opacity-50' : ''}`}
    >
      <div className="flex min-w-0 flex-grow flex-col">
        <Switch.Label as="span" className="text-sm font-medium text-gray-900" passive>
          {label}
        </Switch.Label>
        {description && (
          <Switch.Description as="span" className="text-sm text-gray-500">
            {description}
          </Switch.Description>
        )}
      </div>
      <Switch
        checked={enabled}
        onChange={onChange}
        disabled={disabled}
        className={`${
          enabled ? 'bg-primary-600' : 'bg-gray-200'
        } relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <span
          aria-hidden="true"
          className={`${
            enabled ? 'translate-x-5' : 'translate-x-0'
          } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
        />
      </Switch>
    </Switch.Group>
  );

  return (
    <div className="space-y-6">
      {error && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Réglage principal mis en avant : l'icône reflète l'état courant */}
      <div className="flex items-center gap-4 rounded-lg bg-gray-50 p-4 ring-1 ring-inset ring-gray-200">
        <span
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition duration-150 ${
            settings.enabled ? 'bg-primary-50 text-primary-600' : 'bg-gray-200 text-gray-500'
          }`}
          aria-hidden="true"
        >
          {settings.enabled ? <BellIcon className="h-5 w-5" /> : <BellSlashIcon className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-grow">
          <ToggleSwitch
            enabled={settings.enabled}
            onChange={() => handleToggle('enabled')}
            label="Activer les notifications"
            description="Nouveaux messages et mises à jour importantes, directement dans votre navigateur"
          />
        </div>
      </div>

      {/* Options dépendantes : rail à gauche et lignes séparées par un filet */}
      <div className="ml-5 divide-y divide-gray-200 border-l-2 border-gray-200 pl-5">
        <div className="pb-4">
          <ToggleSwitch
            enabled={settings.enabled && settings.sound}
            onChange={() => settings.enabled && handleToggle('sound')}
            disabled={!settings.enabled}
            label="Son"
            description="Jouer un son lors de la réception d'une notification"
          />
        </div>

        <div className="py-4">
          <ToggleSwitch
            enabled={settings.enabled && settings.newMessage}
            onChange={() => settings.enabled && handleToggle('newMessage')}
            disabled={!settings.enabled}
            label="Nouveaux messages"
            description="Notifications pour les nouveaux messages reçus"
          />
        </div>

        <div className="py-4">
          <ToggleSwitch
            enabled={settings.enabled && settings.mentions}
            onChange={() => settings.enabled && handleToggle('mentions')}
            disabled={!settings.enabled}
            label="Mentions"
            description="Notifications lorsque vous êtes mentionné dans un message"
          />
        </div>

        <div className="pt-4">
          <ToggleSwitch
            enabled={settings.enabled && settings.status}
            onChange={() => settings.enabled && handleToggle('status')}
            disabled={!settings.enabled}
            label="Mises à jour de statut"
            description="Notifications pour les changements de statut de connexion"
          />
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
